import React, { useEffect, useRef, useState } from 'react';
import { HiMinus } from "react-icons/hi";
import { FiAlertTriangle } from 'react-icons/fi';
import StackedCards from '../../../../components/StackedCards';
import { getCompletionStyles } from '../../../../utils/taskUtils';
import { HighlightedText } from '../../../../utils/highlightUtils';
import { replaceZIndexStyle } from "../../utils";
import TaskHoverTooltip from '../../../../components/common/TaskHoverTooltip';
import TaskCardTooltipContent from './TaskCardTooltipContent';
import { dashboardStatusHexColors, getDashboardStatusLabel, DashboardStatus } from '../../../../constants/integrationTypes';

interface Task {
  item_id: number;
  task_title: string;
  project_name: string;
  project_color: string;
  duration: number;
  completion?: number;
  start: string;
  end: string;
  project_id?: string;
  due_date?: string | null;
  status?: string;
  status_label?: string;
  status_color?: string;
  progress?: number;
  integration_item_type?: string;
  integration_team_name?: string;
  integration_project_name?: string;
  closed_outside_sprint?: boolean;
}

interface TaskBarProps {
  ongoingTask: Task;
  tasksForDate: Task[];
  barWidth: number;
  barStyle: any;
  boxheight: number;
  boxWidth: number;
  searchText: string;
  group: any;
  areTasksOnSameDate: (tasks: Task[]) => boolean;
  onElementClick: (e: React.MouseEvent | null, ele: Task) => void;
  onDeleteClick: (e: React.MouseEvent | null, tasksData: Task, group: any) => void;
  isSingleTeamSelected?: boolean;
  isSprintFilter?: boolean;
}

const TaskBar: React.FC<TaskBarProps> = ({
  ongoingTask,
  tasksForDate,
  barWidth,
  barStyle,
  boxheight,
  boxWidth,
  searchText,
  group,
  areTasksOnSameDate,
  onElementClick,
  onDeleteClick,
  isSingleTeamSelected = false,
  isSprintFilter = false
}) => {
  // Track hover state so we can remove styles programmatically on scroll
  const [isHovered, setIsHovered] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formatDate = (value?: string | null) => {
    if (!value) return 'Not set';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 'Not set' : parsed.toLocaleDateString();
  };
  const getStatusInfo = (task: Task) => {
    const statusKey = task.status?.toLowerCase?.() || '';
    const derivedLabel = statusKey ? getDashboardStatusLabel(statusKey) : undefined;
    const label = derivedLabel || 'Not set';
    const color = (statusKey ? dashboardStatusHexColors[statusKey] : undefined) || '#a3a3a3';
    return { label, color };
  };

  const getElements = () => {
    const container = document.getElementById('box-container');
    const rightPane = document.getElementById('right-pane');
    const leftPanel = document.getElementById('left-panel');
    const resourcesText = document.getElementById('resources-text');
    return { container, rightPane, leftPanel, resourcesText };
  };

  const updateZIndices = (zRightPane: number, zLeftPanel: number, zResourcesText: number) => {
    const { rightPane, leftPanel, resourcesText } = getElements();
    if (!rightPane || !leftPanel || !resourcesText) return;

    replaceZIndexStyle(rightPane, zRightPane);
    replaceZIndexStyle(leftPanel, zLeftPanel);
    replaceZIndexStyle(resourcesText, zResourcesText);
  };

  useEffect(() => {
    const { container } = getElements()
    if (!container) return;

    const handleScroll = () => {
      if (isHovered && container.scrollLeft === 0) {
        setIsHovered(false);
        updateZIndices(15, 20, 30)
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [isHovered]);

  useEffect(() => {
    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    }
  }, []);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setIsHovered(true);

    const { container } = getElements()
    if (!container) return;

    const target = event.target as HTMLElement
    if (target.closest('#group-0') === null || container.scrollTop !== 0) return;

    updateZIndices(9995, 9996, 9997)
  }

  const handleMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
    setIsHovered(false);

    const { container } = getElements()
    if (!container) return;

    const target = event.target as HTMLElement
    if (target.closest('#group-0') === null || container.scrollTop !== 0) return;

    updateZIndices(15, 20, 30)
  }

  // If the bar corresponds to tasks that start on this date, keep it above any spanning bar
  const startsToday = tasksForDate && tasksForDate.length > 0;

  const status = ongoingTask?.status?.toLowerCase?.() || '';
  const { label: statusLabel, color: statusColor } = getStatusInfo(ongoingTask);
  const showRiskTriangle = status === DashboardStatus.DELAYED || status === DashboardStatus.UNALLOCATED || status === DashboardStatus.BLOCKED;
  const showClosedOutsideIndicator = isSprintFilter && ongoingTask.closed_outside_sprint;

  return (
    <div
      className="absolute"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        left: 0,
        width: `${barWidth}px`,
        zIndex: isHovered ? 90 : (startsToday ? 35 : 20),
      }}
    >
      {areTasksOnSameDate(tasksForDate) ? (
        <StackedCards
          currentTask={tasksForDate[0] as any}
          overlappingTasks={tasksForDate.slice(1) as any}
          onTaskClick={(task) => onElementClick(null, task as any)}
          onDeleteTask={(task) => onDeleteClick(null, task as any, group)}
          searchText={searchText}
          boxWidth={boxWidth}
          resourceName={group?.title}
          onAllTasksOpen={() => {
            setIsHovered(false);
            updateZIndices(15, 20, 30);
          }}
          isSingleTeamSelected={isSingleTeamSelected}
          isSprintFilter={isSprintFilter}
        />
      ) : (
        <TaskHoverTooltip
          placement="right"
          arrow
          title={
            <TaskCardTooltipContent
              task={ongoingTask as any}
              statusLabel={statusLabel}
              statusColor={statusColor}
              isSprintFilter={isSprintFilter}
              isSingleTeamSelected={isSingleTeamSelected}
            />
          }
        >
          <div
            className="group flex flex-wrap px-2 py-0 top-[2px] rounded-lg cursor-pointer transition-all duration-200 relative"
            onClick={() => onElementClick(null, ongoingTask as any)}
            style={{
              background: `linear-gradient(135deg, ${ongoingTask.project_color}dd, ${ongoingTask.project_color}99)`,
              width: '100%',
              maxHeight: `${boxheight}px`,
              height: `${boxheight - 4}px`,
              boxSizing: 'border-box',
              transform: isHovered ? 'translateY(-1px) scale(1.003)' : 'translateY(0)',
              transition: 'all 0.2s ease-in-out',
              position: 'relative',
              zIndex: isHovered ? 30 : (startsToday ? 25 : 15),
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: isHovered
                ? '0 4px 12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.15)'
                : '0 1px 3px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)',
              overflow: 'visible',
              ...barStyle
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none rounded-lg"
              style={{
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.2))',
                opacity: 0.4
              }}
            />

            <div
              className="whitespace-nowrap text-ellipsis overflow-hidden w-full text-white relative z-10"
              style={{
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}
            >
              <span className="text-sm font-normal font-['Inter'] leading-[21px]">
                {showRiskTriangle && (
                  <span title="This is contributing to risk" className="mt-1 mr-1 inline-flex items-center flex-shrink-0">
                    <FiAlertTriangle className="text-red-500 w-3 h-3" style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))' }} />
                  </span>
                )}
                {showClosedOutsideIndicator && (
                  <span title="Closed outside sprint" className="mt-1 mr-1 inline-flex items-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l2 2 4-4" />
                    </svg>
                  </span>
                )}
                <HighlightedText
                  text={ongoingTask.task_title}
                  searchTerm={searchText}
                /> (Work Item #{
                  searchText && ongoingTask.item_id.toString().includes(searchText.toLowerCase())
                    ? <HighlightedText
                      text={ongoingTask.item_id.toString()}
                      searchTerm={searchText}
                    />
                    : ongoingTask.item_id
                }){" "}
              </span>
              <span className="text-sm font-semibold font-['Inter'] leading-[21px]">
                <HighlightedText
                  text={ongoingTask.project_name}
                  searchTerm={searchText}
                />: {" "}
              </span>

            </div>
            <div className="flex justify-between items-end w-full mt-1 relative z-10">
              <span
                className="text-white/90 text-xs font-medium font-['Inter'] leading-[18px]"
                style={{
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}
              >
                {ongoingTask.duration}d
              </span>
              {ongoingTask.completion !== undefined && (
                <div
                  className="flex items-center px-1.5 py-0.5 rounded text-xs font-bold text-white [text-shadow:0_0_8px_rgba(0,0,0,0.3)]"
                  style={getCompletionStyles(ongoingTask.completion, statusColor)}
                >
                  {ongoingTask.completion}%
                </div>
              )}
            </div>
            <button
              className="hidden group-hover:flex absolute bg-red-600/95 hover:bg-red-700 p-1.5 right-[-8px] top-[-8px] cursor-pointer rounded-full shadow-lg transition-all duration-200 backdrop-blur-sm border border-red-400/30 items-center justify-center hover:scale-110"
              style={{ zIndex: 9999 }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(null, ongoingTask as any, group);
              }}
              title="Remove work item"
            >
              <HiMinus className="text-white w-3 h-3" />
            </button>
          </div>
        </TaskHoverTooltip>
      )}
    </div>
  );
};

export default TaskBar; 